import { useMemo } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import { EntityDisplay } from "@components/entity/EntityDisplay";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { CheckboxIndicator } from "@design-system/Checkbox/CheckboxIndicator";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { Radio, RadioGroup } from "@design-system/Radio/Radio";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useCreateLedger } from "@api/ledger.api";
import { LEDGER_NAME_MAX_LENGTH } from "@common/contracts/ledger.contract";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { Entity } from "@common/types/entity";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import styles from "./AccountingSettings.module.scss";

const NewLedgerFormSchema = z.object({
    entityId: z.coerce.number(),
    name: z
        .string()
        .trim()
        .min(1, "Ledger name is required")
        .max(LEDGER_NAME_MAX_LENGTH),
    basis: z.nativeEnum(AccountingLedgerBasis),
    isPrimary: z.boolean(),
});

type NewLedgerFormValues = z.infer<typeof NewLedgerFormSchema>;

const fields = createFormikFields<NewLedgerFormValues>();

interface Props {
    entities: Entity[];
    initialEntityId?: Entity["id"];
    show: boolean;
    onHide: () => void;
}

export const NewLedgerModal: React.FC<Props> = ({
    entities,
    initialEntityId,
    show,
    onHide,
}) => {
    const { error: errorToast } = useToaster();
    const createLedger = useCreateLedger();

    const formik = useFormik<NewLedgerFormValues>({
        initialValues: {
            entityId: initialEntityId ?? entities[0]?.id ?? 0,
            name: "",
            basis: AccountingLedgerBasis.accruals,
            isPrimary: false,
        },
        enableReinitialize: true,
        validate: validateFormikWithZodSchema(NewLedgerFormSchema),
        validateOnMount: true,
        onSubmit: submitHelper({
            loading: createLedger.isPending,
            handler: async (values) => {
                await createLedger.mutateAsync({
                    params: { entityId: values.entityId },
                    body: {
                        basis: values.basis,
                        name: values.name.trim(),
                        isPrimary: values.isPrimary,
                    },
                });
                formik.resetForm();
                onHide();
            },
            failureHandler: async () => {
                errorToast("Failed to create ledger");
            },
        }),
    });

    const entityItems = useMemo(
        () =>
            entities.map((entity) => ({
                value: entity.id,
                label: entity.name ?? "",
                leading: <EntityAvatar entity={entity} size="xs" aria-hidden />,
            })),
        [entities],
    );
    const selectedEntity = entities.find(
        (entity) => entity.id === formik.values.entityId,
    );
    const handleEntityChange = (entityId: number | null) => {
        if (entityId) {
            void formik.setFieldValue("entityId", entityId);
        }
    };
    const handlePrimaryToggle = () => {
        void formik.setFieldValue("isPrimary", !formik.values.isPrimary);
    };
    const handleHide = () => {
        formik.resetForm();
        onHide();
    };

    return (
        <PlainModal show={show} onHide={handleHide} size="md">
            <StandardModalHeader>New ledger</StandardModalHeader>
            <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                    <VStack gap={16}>
                        <FormikField
                            name="entityId"
                            label="Entity"
                            control="button"
                        >
                            {() => (
                                <Picker.Select
                                    items={entityItems}
                                    value={formik.values.entityId}
                                    onValueChange={handleEntityChange}
                                    renderValue={() =>
                                        selectedEntity ? (
                                            <EntityDisplay
                                                entity={selectedEntity}
                                            />
                                        ) : undefined
                                    }
                                    placeholder="Select entity"
                                    trigger={
                                        <Picker.Button
                                            variant="primary"
                                            size="xl"
                                        />
                                    }
                                />
                            )}
                        </FormikField>
                        <VStack gap={16}>
                            <fields.Text
                                name="name"
                                label="Ledger name"
                                placeholder="e.g. Accrual GAAP"
                                size="xl"
                            />
                            <VStack gap={8}>
                                <div>
                                    <span className={styles.methodLabel}>
                                        Accounting method
                                    </span>
                                    <RadioGroup
                                        name="basis"
                                        aria-label="Accounting method"
                                        value={formik.values.basis}
                                        onValueChange={(basis) => {
                                            if (
                                                basis ===
                                                    AccountingLedgerBasis.cash ||
                                                basis ===
                                                    AccountingLedgerBasis.accruals
                                            ) {
                                                void formik.setFieldValue(
                                                    "basis",
                                                    basis,
                                                );
                                            }
                                        }}
                                        className={styles.methodOptions}
                                    >
                                        <Radio
                                            value={AccountingLedgerBasis.cash}
                                            className={styles.methodOption}
                                            label={
                                                <span>
                                                    <span className="font-weight-medium">
                                                        Cash
                                                    </span>
                                                    <small>
                                                        Record income and
                                                        expenses when money
                                                        moves
                                                    </small>
                                                </span>
                                            }
                                        />
                                        <Radio
                                            value={
                                                AccountingLedgerBasis.accruals
                                            }
                                            className={styles.methodOption}
                                            label={
                                                <span>
                                                    <span className="font-weight-medium">
                                                        Accrual
                                                    </span>
                                                    <small>
                                                        Record income and
                                                        expenses when earned
                                                    </small>
                                                </span>
                                            }
                                        />
                                    </RadioGroup>
                                </div>
                                <button
                                    type="button"
                                    className={styles.primaryOption}
                                    role="checkbox"
                                    aria-checked={formik.values.isPrimary}
                                    onClick={handlePrimaryToggle}
                                >
                                    <CheckboxIndicator
                                        state={
                                            formik.values.isPrimary
                                                ? "checked"
                                                : "unchecked"
                                        }
                                        className="mt-1"
                                    />
                                    <span>
                                        <span className="font-weight-medium">
                                            Set as primary ledger
                                        </span>
                                        <small>
                                            Other ledgers with this method will
                                            mirror changes going forward
                                        </small>
                                    </span>
                                </button>
                            </VStack>
                        </VStack>
                    </VStack>
                    <HStack as="footer" justify="end" gap={12}>
                        <Button
                            variant="secondary"
                            size="xl"
                            type="button"
                            onClick={handleHide}
                            disabled={createLedger.isPending}
                        >
                            Cancel
                        </Button>
                        <ButtonWithLoader
                            variant="primary"
                            size="xl"
                            type="submit"
                            loading={createLedger.isPending}
                            disabled={!formik.isValid}
                        >
                            Create
                        </ButtonWithLoader>
                    </HStack>
                </Form>
            </FormikProvider>
        </PlainModal>
    );
};
